import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Popover, PopoverContent, PopoverTrigger,
} from './ui/popover';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Separator } from './ui/separator';
import { ScrollArea } from './ui/scroll-area';
import { Database, RefreshCw, CheckCircle2, AlertCircle, Clock, Loader2, TrendingUp, TrendingDown, Minus } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

function timeAgo(iso) {
  if (!iso) return 'never';
  const secs = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
  if (secs < 60) return `${Math.floor(secs)}s ago`;
  if (secs < 3600) return `${Math.floor(secs / 60)}m ago`;
  if (secs < 86400) return `${Math.floor(secs / 3600)}h ago`;
  return `${Math.floor(secs / 86400)}d ago`;
}

function statusColor(entry) {
  if (!entry) return 'bg-slate-400';
  const errors = Object.values(entry.per_collection || {}).filter(v => v.status === 'error').length;
  if (errors > 0) return 'bg-amber-500';
  return 'bg-emerald-500';
}

export default function SyncStatusIndicator({ token }) {
  const [status, setStatus] = useState(null);
  const [open, setOpen] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [error, setError] = useState(null);

  const fetchStatus = useCallback(async () => {
    if (!token) return;
    try {
      const res = await axios.get(`${BACKEND_URL}/api/admin/db-sync/status`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setStatus(res.data);
      setError(null);
    } catch (err) {
      if (err.response?.status === 403 || err.response?.status === 401) {
        setStatus({ notAllowed: true });
      } else {
        setError(err.response?.data?.detail || 'Failed to load sync status');
      }
    }
  }, [token]);

  useEffect(() => {
    fetchStatus();
    // Poll every 60s while mounted
    const iv = setInterval(fetchStatus, 60000);
    return () => clearInterval(iv);
  }, [fetchStatus]);

  const triggerSync = async () => {
    setSyncing(true);
    try {
      const res = await axios.post(`${BACKEND_URL}/api/admin/db-sync/from-prod`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success(`Synced ${res.data.docs_copied} docs across ${res.data.collections_synced} collections in ${res.data.duration_seconds}s`);
      fetchStatus();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Sync failed');
    } finally {
      setSyncing(false);
    }
  };

  // Hide entirely if not configured or unauthorized
  if (!status || status.notAllowed || !status.configured) return null;

  const latest = status.history?.[0];
  const dot = statusColor(latest);
  const timeStr = latest ? timeAgo(latest.finished_at || latest.started_at) : 'never';
  const totalErrors = latest
    ? Object.values(latest.per_collection || {}).filter(v => v.status === 'error').length
    : 0;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          data-testid="sync-status-indicator"
          className="hidden md:flex items-center gap-1.5 px-2 py-1 rounded-md hover:bg-muted/60 transition-colors text-[10px] text-muted-foreground shrink-0"
          title={`Prod DB → Preview sync: ${timeStr}${totalErrors ? ` (${totalErrors} errors)` : ''}`}
        >
          <span className="relative flex h-2 w-2 shrink-0">
            <span className={`absolute inline-flex h-full w-full rounded-full ${dot} opacity-40 animate-ping`} />
            <span className={`relative inline-flex h-2 w-2 rounded-full ${dot}`} />
          </span>
          <Database className="h-3 w-3" />
          <span className="hidden lg:inline font-medium">Prod {timeStr}</span>
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-96 p-0" data-testid="sync-status-popover">
        <div className="p-3 border-b bg-muted/20">
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2 min-w-0">
              <Database className="h-4 w-4 text-primary shrink-0" />
              <div className="min-w-0">
                <div className="text-sm font-semibold">Prod → Preview Sync</div>
                <div className="text-[10px] text-muted-foreground">One-way mirror of production data</div>
              </div>
            </div>
            <Button
              size="sm"
              variant="outline"
              className="gap-1.5 text-xs h-7 shrink-0"
              onClick={triggerSync}
              disabled={syncing}
              data-testid="sync-trigger-btn"
            >
              {syncing ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3" />}
              {syncing ? 'Syncing...' : 'Sync now'}
            </Button>
          </div>
        </div>

        {latest ? (
          <div className="p-3 border-b space-y-1.5">
            <div className="flex items-center gap-2">
              {totalErrors > 0 ? (
                <AlertCircle className="h-3.5 w-3.5 text-amber-500" />
              ) : (
                <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500" />
              )}
              <span className="text-xs font-semibold">Last sync: {timeStr}</span>
              <Badge variant="secondary" className="ml-auto font-mono text-[10px]">
                {latest.duration_seconds}s
              </Badge>
            </div>
            <div className="grid grid-cols-3 gap-2 text-[10px] pt-1">
              <div className="rounded-md bg-muted/40 p-2">
                <div className="text-muted-foreground uppercase tracking-wider">Collections</div>
                <div className="text-sm font-bold tabular-nums">{latest.collections_synced}</div>
              </div>
              <div className="rounded-md bg-muted/40 p-2">
                <div className="text-muted-foreground uppercase tracking-wider">Docs</div>
                <div className="text-sm font-bold tabular-nums">{latest.docs_copied?.toLocaleString?.() ?? latest.docs_copied}</div>
              </div>
              <div className="rounded-md bg-muted/40 p-2">
                <div className="text-muted-foreground uppercase tracking-wider">Skipped</div>
                <div className="text-sm font-bold tabular-nums">{latest.collections_skipped}</div>
              </div>
            </div>

            {/* Delta headline — what changed since last sync */}
            {(latest.docs_added > 0 || latest.docs_removed > 0) ? (
              <div className="flex items-center gap-2 pt-1 text-[10px]" data-testid="sync-delta-headline">
                <span className="text-muted-foreground uppercase tracking-wider font-semibold">Δ since last sync:</span>
                {latest.docs_added > 0 && (
                  <span className="flex items-center gap-0.5 text-emerald-600 font-medium tabular-nums">
                    <TrendingUp className="h-3 w-3" /> +{latest.docs_added.toLocaleString?.() ?? latest.docs_added}
                  </span>
                )}
                {latest.docs_removed > 0 && (
                  <span className="flex items-center gap-0.5 text-rose-600 font-medium tabular-nums">
                    <TrendingDown className="h-3 w-3" /> −{latest.docs_removed.toLocaleString?.() ?? latest.docs_removed}
                  </span>
                )}
                <span className="ml-auto text-muted-foreground">
                  across {latest.changed_collections} coll{latest.changed_collections !== 1 ? 's' : ''}
                </span>
              </div>
            ) : latest.changed_collections === 0 ? (
              <div className="flex items-center gap-1.5 pt-1 text-[10px] text-muted-foreground">
                <Minus className="h-3 w-3" />
                No document count changes since last sync
              </div>
            ) : null}

            <div className="text-[10px] text-muted-foreground pt-1 flex items-center gap-1">
              <Clock className="h-3 w-3" />
              Trigger: <span className="font-medium">{latest.triggered_by || 'unknown'}</span>
              <span className="ml-auto font-mono">{latest.prod_db} → {latest.local_db}</span>
            </div>
            {totalErrors > 0 && (
              <div className="text-[10px] text-amber-700 bg-amber-50 dark:bg-amber-950/30 rounded p-1.5 border border-amber-200">
                {totalErrors} collection{totalErrors !== 1 ? 's' : ''} failed to sync
              </div>
            )}
          </div>
        ) : (
          <div className="p-4 text-center text-xs text-muted-foreground">
            No sync history yet. Click &quot;Sync now&quot; to run the first one.
          </div>
        )}

        {/* Per-collection diff — collections whose count changed since last sync */}
        {latest && latest.per_collection && (() => {
          const changed = Object.entries(latest.per_collection)
            .filter(([_, v]) => v.status === 'ok' && v.delta !== 0)
            .sort((a, b) => Math.abs(b[1].delta) - Math.abs(a[1].delta));
          if (changed.length === 0) return null;
          return (
            <div data-testid="per-collection-diff">
              <div className="px-3 py-1.5 text-[10px] font-semibold text-muted-foreground uppercase tracking-wider bg-muted/20 flex items-center justify-between">
                <span>What changed</span>
                <span className="font-mono tabular-nums normal-case tracking-normal">{changed.length} collection{changed.length !== 1 ? 's' : ''}</span>
              </div>
              <ScrollArea className="max-h-40">
                <div className="p-2 space-y-0.5">
                  {changed.map(([name, v]) => {
                    const delta = v.delta;
                    const isAdd = delta > 0;
                    const isDel = delta < 0;
                    const color = isAdd ? 'text-emerald-600' : isDel ? 'text-rose-600' : 'text-muted-foreground';
                    const Icon = isAdd ? TrendingUp : isDel ? TrendingDown : Minus;
                    return (
                      <div key={name} className="flex items-center gap-2 text-[10px] py-1 px-1.5 rounded hover:bg-muted/40" data-testid={`diff-row-${name}`}>
                        <Icon className={`h-3 w-3 shrink-0 ${color}`} />
                        <span className="font-mono flex-1 truncate">{name}</span>
                        <span className="text-muted-foreground tabular-nums">{v.previous}</span>
                        <span className="text-muted-foreground">→</span>
                        <span className="font-medium tabular-nums">{v.docs}</span>
                        <span className={`font-bold tabular-nums w-10 text-right ${color}`}>
                          {isAdd ? '+' : ''}{delta}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </ScrollArea>
            </div>
          );
        })()}

        {status.history?.length > 1 && (
          <div>
            <div className="px-3 py-1.5 text-[10px] font-semibold text-muted-foreground uppercase tracking-wider bg-muted/20">
              Recent history
            </div>
            <ScrollArea className="max-h-48">
              <div className="p-2 space-y-1">
                {status.history.slice(1, 10).map((h, i) => {
                  const errs = Object.values(h.per_collection || {}).filter(v => v.status === 'error').length;
                  return (
                    <div key={i} className="flex items-center gap-2 text-[10px] py-1 px-1.5 rounded hover:bg-muted/40" data-testid={`sync-history-row-${i}`}>
                      <span className={`h-1.5 w-1.5 rounded-full ${errs ? 'bg-amber-500' : 'bg-emerald-500'} shrink-0`} />
                      <span className="text-muted-foreground shrink-0">{timeAgo(h.finished_at || h.started_at)}</span>
                      <Separator orientation="vertical" className="h-3" />
                      <span className="font-mono tabular-nums">{h.docs_copied?.toLocaleString?.() ?? h.docs_copied} docs</span>
                      <span className="text-muted-foreground">·</span>
                      <span className="font-mono tabular-nums">{h.duration_seconds}s</span>
                      <span className="ml-auto text-[9px] text-muted-foreground">{h.triggered_by}</span>
                    </div>
                  );
                })}
              </div>
            </ScrollArea>
          </div>
        )}
        {error && (
          <div className="p-2 text-[10px] text-destructive bg-destructive/10">{error}</div>
        )}
      </PopoverContent>
    </Popover>
  );
}
